import { useCallback, useState } from 'react';

const STEP_KEYS = {
    profileImage: 'onboarding-dashboard.step.profile-image.ready-for-next',
    socialLinks: 'onboarding-dashboard.step.social-links.ready-for-next',
    depositSetup: 'onboarding-dashboard.step.deposit-setup.ready-for-next',
    firstProduct: 'onboarding-dashboard.step.first-product.ready-for-next',
} as const;

type StepKey = keyof typeof STEP_KEYS;

/**
 * Hook to manage onboarding step readiness state in sessionStorage
 * Provides reactive getters and setters for step completion status
 */
export function useOnboardingSessionStorage() {
    const [refreshKey, setRefreshKey] = useState(0);

    const getStepReady = useCallback((step: StepKey): boolean => {
        return sessionStorage.getItem(STEP_KEYS[step]) === '1';
    }, []);

    const setStepReady = useCallback((step: StepKey, ready: boolean = true) => {
        if (ready) {
            sessionStorage.setItem(STEP_KEYS[step], '1');
        } else {
            sessionStorage.removeItem(STEP_KEYS[step]);
        }
        // Force re-render to update components using this hook
        setRefreshKey(prev => prev + 1);
    }, []);

    const markStepReady = useCallback(
        (step: StepKey) => {
            setStepReady(step, true);
        },
        [setStepReady]
    );

    const clearStepReady = useCallback(
        (step: StepKey) => {
            setStepReady(step, false);
        },
        [setStepReady]
    );

    // Get all step readiness states
    const getAllStepsReady = useCallback(() => {
        return {
            profileImage: getStepReady('profileImage'),
            socialLinks: getStepReady('socialLinks'),
            depositSetup: getStepReady('depositSetup'),
            firstProduct: getStepReady('firstProduct'),
        };
    }, [getStepReady]);

    return {
        getStepReady,
        setStepReady,
        markStepReady,
        clearStepReady,
        getAllStepsReady,
        refreshKey, // Expose refreshKey for components that need to trigger re-renders
    };
}
